import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { NAV_LINKS, type NavId } from '../../lib/content'
import { Logo } from '../ui/Logo'
import { SmartLink } from '../ui/SmartLink'
import './Nav.css'

/** Floating glass nav: always on top, shrinks on scroll, highlights where you are, and becomes a menu on phones. */
export function Nav() {
  const { pathname } = useLocation()
  const onHome = pathname === '/'
  const [small, setSmall] = useState(false)
  const [spy, setSpy] = useState<NavId>('top')
  // The menu remembers which page it was opened on, so it closes itself after navigation.
  const [openOn, setOpenOn] = useState<string | null>(null)
  const open = openOn === pathname
  const setOpen = useCallback((v: boolean | ((o: boolean) => boolean)) => {
    setOpenOn((cur) => {
      const was = cur === pathname
      const next = typeof v === 'function' ? v(was) : v
      return next ? pathname : null
    })
  }, [pathname])
  const tabsRef = useRef<HTMLDivElement>(null)
  const thumbRef = useRef<HTMLSpanElement>(null)
  const navRef = useRef<HTMLElement>(null)
  // While the page scrolls to a clicked section, keep the highlight on that tab instead of sliding through the ones in between.
  const pinned = useRef<{ id: NavId; until: number; max: number } | null>(null)
  const pin = useCallback((id: NavId) => {
    const now = Date.now()
    pinned.current = { id, until: now + 400, max: now + 4000 }
    if (NAV_LINKS.find((l) => l.id === id)?.spy) setSpy(id)
  }, [])
  const active: NavId | null = onHome ? spy : pathname.startsWith('/advertise') ? 'advertise' : null

  const moveThumb = useCallback((id: NavId | null) => {
    const t = thumbRef.current
    if (!t) return
    const a = id ? tabsRef.current?.querySelector<HTMLAnchorElement>(`a[data-id="${id}"]`) : null
    if (!a || !a.offsetWidth) {
      t.style.opacity = '0'
      return
    }
    t.style.opacity = '1'
    t.style.left = `${a.offsetLeft}px`
    t.style.width = `${a.offsetWidth}px`
  }, [])

  // Scroll: float the bar, and on the home page work out which section is on screen.
  useEffect(() => {
    let raf = 0
    const check = () => {
      raf = 0
      setSmall(window.scrollY > 90)
      if (!onHome) return
      const p = pinned.current
      if (p) {
        const now = Date.now()
        // Stay pinned while the smooth scroll is still moving (scroll events keep arriving), up to 4 seconds.
        if (now < p.until && now < p.max) {
          p.until = now + 250
          return
        }
        pinned.current = null
      }
      let cur: NavId = 'top'
      for (const l of NAV_LINKS) {
        if (!l.spy) continue
        const el = document.getElementById(l.spy)
        if (el && el.getBoundingClientRect().top < window.innerHeight * 0.4) cur = l.id
      }
      setSpy(cur)
    }
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(check)
    }
    check()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [onHome])

  useLayoutEffect(() => moveThumb(active), [active, small, moveThumb])

  // Re-measure when fonts load or the bar finishes resizing.
  useEffect(() => {
    const nav = navRef.current
    const tabs = tabsRef.current
    const again = () => moveThumb(active)
    document.fonts?.ready.then(again).catch(() => undefined)
    const ro = typeof ResizeObserver !== 'undefined' && tabs ? new ResizeObserver(again) : null
    if (ro && tabs) ro.observe(tabs)
    nav?.addEventListener('transitionend', again)
    return () => {
      ro?.disconnect()
      nav?.removeEventListener('transitionend', again)
    }
  }, [active, moveThumb])

  // While the phone menu is open: Escape closes it, and the page behind doesn't scroll.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    window.addEventListener('keydown', onKey)
    const root = document.documentElement
    const prev = root.style.overflow
    root.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      root.style.overflow = prev
    }
  }, [open, setOpen])

  return (
    <>
      <header className="navbar">
        <nav ref={navRef} className={`nav glass${small ? ' small' : ''}`} aria-label="Main">
          <Logo />
          <div className="tabs" ref={tabsRef}>
            <span className="thumb" ref={thumbRef} />
            {NAV_LINKS.map((l) => (
              <SmartLink key={l.id} to={l.to} data-id={l.id} className={active === l.id ? 'on' : undefined} aria-current={active === l.id ? 'page' : undefined} onClick={() => pin(l.id)}>
                {l.label}
              </SmartLink>
            ))}
          </div>
          <SmartLink className="btn gold" to="/#waitlist">Join the waitlist</SmartLink>
          <button className="menu" type="button" aria-label={open ? 'Close menu' : 'Open menu'} aria-expanded={open} aria-controls="sheet" onClick={() => setOpen((o) => !o)}>
            <span /><span />
          </button>
        </nav>
      </header>
      {open && <div className="sheet-backdrop" aria-hidden="true" onClick={() => setOpen(false)} />}
      <div className="sheet glass" id="sheet" hidden={!open}>
        {NAV_LINKS.map((l) => (
          <SmartLink key={l.id} to={l.to} onClick={() => { pin(l.id); setOpen(false) }} aria-current={active === l.id ? 'page' : undefined}>{l.label}</SmartLink>
        ))}
        <SmartLink to="/#waitlist" className="btn gold" style={{ justifyContent: 'center', marginTop: 6 }} onClick={() => setOpen(false)}>Join the waitlist</SmartLink>
      </div>
    </>
  )
}
